import { AtomData, SpeciesGeometry } from '../types/simulation'; // Import types

/**
 * Fetches and parses a file in XYZ format.
 * 
 * XYZ format expected:
 * line 1: Number of atoms (integer)
 * line 2: Comment line (ignored)
 * line 3+: ElementSymbol X Y Z (whitespace separated)
 * 
 * @param url The URL of the .xyz file to fetch.
 * @returns A Promise that resolves to a SpeciesGeometry object containing atom data,
 *          or null if fetching or parsing fails.
 */
export const parseXyzFile = async (url: string): Promise<SpeciesGeometry | null> => {
  console.log(`[XYZ Parser] Attempting to fetch and parse: ${url}`);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(`[XYZ Parser] Failed to fetch ${url}: ${response.status} ${response.statusText}`);
      return null;
    }
    const text = await response.text();
    const lines = text.trim().split('\n');

    if (lines.length < 2) {
      console.error(`[XYZ Parser] Invalid format: File ${url} has less than 2 lines.`);
      return null;
    }

    const atomCount = parseInt(lines[0].trim(), 10);
    if (isNaN(atomCount) || atomCount <= 0) {
      console.error(`[XYZ Parser] Invalid format: First line (atom count) in ${url} is not a positive integer.`);
      return null;
    }

    // Expecting atomCount lines + 2 header lines
    if (lines.length < atomCount + 2) {
       console.warn(`[XYZ Parser] Warning: File ${url} has fewer lines (${lines.length}) than expected for ${atomCount} atoms (expected ${atomCount + 2}). Parsing available lines.`);
       // Adjust expected lines if fewer are present, but still require at least the header
    }

    const atoms: AtomData[] = [];
    // Start parsing from the 3rd line (index 2)
    for (let i = 2; i < lines.length && atoms.length < atomCount; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const parts = line.split(/\s+/); // Split by one or more whitespace characters
      if (parts.length < 4) {
        console.error(`[XYZ Parser] Invalid format: Line ${i + 1} in ${url} has fewer than 4 columns.`);
        return null; // Or continue and skip line? Returning null for stricter parsing.
      }

      const element = parts[0];
      const x = parseFloat(parts[1]);
      const y = parseFloat(parts[2]);
      const z = parseFloat(parts[3]);

      if (isNaN(x) || isNaN(y) || isNaN(z)) {
        console.error(`[XYZ Parser] Invalid format: Non-numeric coordinates found on line ${i + 1} in ${url}.`);
        return null;
      }

      atoms.push({ element, position: [x, y, z] });
    }

    if (atoms.length !== atomCount) {
      console.error(`[XYZ Parser] Mismatch: Expected ${atomCount} atoms but parsed ${atoms.length} from ${url}.`);
      // Allow parsing to proceed but log error. Or return null? Let's be strict for now.
      return null; 
    }
    
    console.log(`[XYZ Parser] Successfully parsed ${atoms.length} atoms from ${url}`);
    return { atoms };

  } catch (error) {
    console.error(`[XYZ Parser] Error fetching or parsing ${url}:`, error);
    return null;
  }
}; 